import React from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { Box, Button, Card, CardContent, Grid, Typography } from '@mui/material';
import { FeedbackInbox } from '../shared/FeedbackInbox';
import BaseLayout from './BaseLayout';
import DashboardIcon from '@mui/icons-material/Dashboard';
import AssignmentIcon from '@mui/icons-material/Assignment';
import BarChartIcon from '@mui/icons-material/BarChart';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import PeopleIcon from '@mui/icons-material/People';
import SettingsIcon from '@mui/icons-material/Settings';
import QuizIcon from '@mui/icons-material/Quiz';
import MyAssignments from '../student/MyAssignments';
import MyTeachers from '../student/MyTeachers';
import TeacherModules from '../student/TeacherModules';
import ModuleViewerWithSubmission from '../student/ModuleViewerWithSubmission';
import StudentSettings from '../student/StudentSettings';

const StudentDashboard = () => {
  const navigate = useNavigate();

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          Student Dashboard
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Continue lessons, practice questions, and track your work.
        </Typography>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <AssignmentIcon color="primary" sx={{ fontSize: 40, mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                My Assignments
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Open assigned modules and use practice questions inside each unit.
              </Typography>
              <Button variant="contained" onClick={() => navigate('/student/assignments')}>
                View Assignments
              </Button>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <PeopleIcon color="primary" sx={{ fontSize: 40, mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                My Teachers
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Browse modules assigned by each teacher.
              </Typography>
              <Button variant="outlined" onClick={() => navigate('/student/teachers')}>
                View Teachers
              </Button>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <QuizIcon color="primary" sx={{ fontSize: 40, mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                Practice
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Practice appears inside each assigned lesson unit.
              </Typography>
              <Button variant="outlined" onClick={() => navigate('/student/assignments')}>
                Start Practice
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};
const CompletedWork = () => <div>Completed Work - Coming Soon</div>;
const MyProgress = () => <div>My Progress - Coming Soon</div>;
const Achievements = () => <div>Achievements - Coming Soon</div>;

/**
 * StudentLayout component for students
 * Provides navigation and routes for accessing lessons and completing assignments
 */
const StudentLayout: React.FC = () => {
  const navigationItems = [
    { label: 'My Feedback', path: '/student/feedback', icon: <AssignmentIcon /> },
    {
      label: 'Dashboard',
      path: '/student/dashboard',
      icon: <DashboardIcon />,
    },
    {
      label: 'My Teachers',
      path: '/student/teachers',
      icon: <PeopleIcon />,
    },
    {
      label: 'Assignments',
      path: '/student/assignments',
      icon: <AssignmentIcon />,
    },
    {
      label: 'My Progress',
      path: '/student/progress',
      icon: <BarChartIcon />,
    },
    {
      label: 'Achievements',
      path: '/student/achievements',
      icon: <EmojiEventsIcon />,
    },
    {
      label: 'Settings',
      path: '/student/settings',
      icon: <SettingsIcon />,
    },
  ];

  return (
    <BaseLayout title="Student Portal" navigationItems={navigationItems}>
      <Routes>
        <Route path="feedback" element={<FeedbackInbox />} />
        <Route path="dashboard" element={<StudentDashboard />} />
        <Route path="teachers" element={<MyTeachers />} />
        <Route path="teachers/:teacherId/modules" element={<TeacherModules />} />
        <Route path="modules/:moduleId" element={<ModuleViewerWithSubmission />} />
        <Route path="lessons/:moduleId" element={<ModuleViewerWithSubmission />} />
        <Route path="assignments" element={<MyAssignments />} />
        <Route path="completed" element={<CompletedWork />} />
        <Route path="progress" element={<MyProgress />} />
        <Route path="achievements" element={<Achievements />} />
        <Route path="settings" element={<StudentSettings />} />
        <Route path="/" element={<Navigate to="/student/dashboard" replace />} />
        <Route path="*" element={<Navigate to="/student/dashboard" replace />} />
      </Routes>
    </BaseLayout>
  );
};

export default StudentLayout;
